import { SYSTEM_DEFAULT_DEVICE_MARKER } from '@/tauri/constants';
import type { AudioDevice } from 'src-tauri/src/bindings/AudioDevice';

/**
 * Pure helpers shared by the silent-capture warning layer
 * (`CaptureSilenceRecovery`) and the stop mutation. Store-free on purpose:
 * both callers hand in the slice they already hold.
 */

export type CaptureNeverEnergeticLatches = Record<'mic' | 'speaker', boolean>;

/**
 * The hard stop and the stop-time "no usable audio" gate fire only when NO
 * source has ever produced a sample. A working speaker channel proves capture
 * is alive and the mic silence is a quiet room or a mid-consult pause, not a
 * dead device. Both latches come from the monitor's
 * `seenEnergetic: false` alerts, so a source that never emitted stays `false`
 * and can never trigger a stop — absence of evidence is not silence.
 */
export function isCaptureNeverEnergetic(
  latches: CaptureNeverEnergeticLatches | undefined
): boolean {
  return latches?.mic === true && latches?.speaker === true;
}

/**
 * Human-readable label of the mic actually in use, for device-named copy.
 * Resolves the `__SYSTEM_DEFAULT__` sentinel to the OS default so the user is
 * told which physical device is silent, not "System default". Falls back to
 * the start-time locked name when the selection cannot be resolved (device
 * list stale or empty), and to `undefined` when even that is the sentinel.
 */
export function resolveInUseMicLabel({
  selected,
  devices,
  lockedInputDeviceName,
}: {
  selected: AudioDevice | undefined;
  devices: AudioDevice[] | undefined;
  lockedInputDeviceName?: string;
}): string | undefined {
  const list = devices ?? [];
  const inUseMic =
    selected?.name === SYSTEM_DEFAULT_DEVICE_MARKER
      ? list.find((device) => device.is_default)
      : selected;
  if (inUseMic) {
    return inUseMic.display_name ?? inUseMic.name;
  }
  if (lockedInputDeviceName && lockedInputDeviceName !== SYSTEM_DEFAULT_DEVICE_MARKER) {
    const locked = list.find((device) => device.name === lockedInputDeviceName);
    return locked?.display_name ?? lockedInputDeviceName;
  }
  return undefined;
}

/**
 * Non-sensitive classification of a resolved mic label, for telemetry ONLY.
 *
 * `resolveInUseMicLabel` above deliberately PREFERS `display_name` — the
 * OS-supplied string. On a clinician's machine that is routinely a person's
 * name ("Jane's iPhone Microphone", "Dr Patel's AirPods"), i.e. user content,
 * and CLAUDE.md's telemetry rule is that user-content-bearing payloads never
 * reach Datadog: send a classification plus structured, non-sensitive context.
 * Every prior PHI/PII incident arrived inside a payload that looked innocuous.
 *
 * Each field answers a triage question the raw string was being used for —
 * was a device resolvable at all, was the recording started on the
 * system-default sentinel rather than an explicit pick, and roughly how long
 * the string was (sentinel-ish vs a real device name) — without carrying the
 * string.
 *
 * This is about what LEAVES the machine. The human-readable name is still
 * shown to the clinician on the stopped toast, the reselection prompt and the
 * upload modal; that is the feature, and it is untouched.
 */
export function classifyMicLabelForTelemetry({
  micLabel,
  lockedInputDeviceName,
}: {
  micLabel: string | undefined;
  lockedInputDeviceName: string | undefined;
}): { hasMicLabel: boolean; micLabelLength: number; isSystemDefault: boolean } {
  return {
    hasMicLabel: micLabel !== undefined,
    micLabelLength: micLabel?.length ?? 0,
    // Strictly "started on the sentinel". An absent `lockedStartDevices`
    // (webview reloaded mid-session, legacy path) is NOT system-default — it
    // is unknown — and reads false rather than inventing a fact.
    isSystemDefault: lockedInputDeviceName === SYSTEM_DEFAULT_DEVICE_MARKER,
  };
}
